"use client";

import Image from "@/components/shared/image";
import { useAuth } from "@/components/auth/AuthProvider";
import { useAnalytics } from "@/lib/analytics";
import testimonialsData from "@/lib/testimonials-simple.json";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import { cn } from "@/lib/utils";

interface TestimonialItem {
  thumbnailSrc: string;
  username: string;
  followerCount: string;
  category: string;
}

const TESTIMONIALS: TestimonialItem[] = testimonialsData.testimonials;

function TestimonialCard({ item }: { item: TestimonialItem }) {
  const { themeColor } = useColorTheme();

  return (
    <div className="group relative h-[292px] w-[180px] flex-shrink-0 overflow-hidden rounded-2xl border border-border/40 bg-card sm:h-[360px] sm:w-[224px] lg:h-[420px] lg:w-[260px]">
      {/* Background Image - sized to match display dimensions */}
      <Image
        src={item.thumbnailSrc}
        alt={`@${item.username}`}
        fill
        className="object-cover"
        loading="lazy"
        decoding="async"
        sizes="(max-width: 639px) 180px, (max-width: 1023px) 224px, 260px"
        srcSet={`${item.thumbnailSrc.replace(/\.webp$/, '_180w.webp')} 180w, ${item.thumbnailSrc.replace(/\.webp$/, '_260w.webp')} 260w`}
      />
      
      {/* Bottom Gradient Overlay */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
      
      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 z-10 p-4">
        <div className="flex items-center gap-2 text-white/90">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          <span className="text-[13px] font-semibold">@{item.username}</span>
        </div>

        {item.followerCount && (
          <p className="mt-1 text-[15px] font-bold text-white drop-shadow-lg">
            {item.followerCount} <span className="text-[12px] font-medium text-white/80">followers</span>
          </p>
        )}

        {item.category && (
          <span className={cn(
            "mt-2 inline-flex items-center rounded-full bg-primary-gradient px-2.5 py-1 text-[11px] font-bold text-white",
            themeColor === "yellow" && "!text-black"
          )}>
            {item.category}
          </span>
        )}
      </div>
    </div>
  );
}

function InfiniteScrollRow({ items }: { items: TestimonialItem[] }) {
  const tripleItems = [...items, ...items, ...items];

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex gap-4 animate-scroll-left"
        style={{ width: "fit-content" }}
      >
        {tripleItems.map((item, index) => (
          <TestimonialCard key={`${item.username}-${index}`} item={item} />
        ))}
      </div>
    </div>
  );
}

export default function Testimonials2() {
  const { openAuth } = useAuth();
  const { track } = useAnalytics();
  const { themeColor } = useColorTheme();

  return (
    <section className="w-full overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      {/* Row 1: Header left + Review cards right - in container */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          {/* Header left */}
          <div className="text-left lg:max-w-md">
            <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Trusted by Creators Worldwide
            </p>
            <h2 className="mt-2 whitespace-nowrap text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              <span className="gradient-text">5K+</span> Creators Trust Zapify
            </h2>
            <p className="mt-3 text-base text-muted-foreground sm:text-lg">
              Join thousands of creators and brands who automate, engage, and grow with Zapify.
            </p>
          </div>

          {/* Review cards right - desktop only */}
          <div className="hidden lg:flex flex-col gap-3 sm:flex-row lg:items-start lg:ml-auto">
            {/* G2 Reviews */}
            <div className="relative">
              <a
                href="https://www.g2.com/products/zapify-instagram-dm-automation-tool/reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl border border-border/50 bg-card/50 px-5 py-4 transition-colors hover:bg-card/80"
              >
                <Image
                  src="/g2-logo.png"
                  alt="G2 Reviews"
                  width={40}
                  height={40}
                  className="h-10 w-10 rounded-full object-contain"
                />
                <div className="text-left">
                  <p className="text-xs text-muted-foreground">Rated on</p>
                  <p className="text-sm font-bold text-foreground">G2 Reviews</p>
                  <div className="mt-0.5 flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-3 w-3 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                    <span className="ml-1 text-xs text-muted-foreground">4.9/5</span>
                  </div>
                </div>
              </a>
            </div>

            {/* Trustpilot with arrow on bottom */}
            <div className="relative">
              <a
                href="https://www.trustpilot.com/review/zapify.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl border border-border/50 bg-card/50 px-5 py-4 transition-colors hover:bg-card/80"
              >
                <Image
                  src="/trustpilot.webp"
                  alt="Trustpilot"
                  width={40}
                  height={40}
                  className="h-10 w-10 object-contain"
                  style={{ maxWidth: '40px', maxHeight: '40px' }}
                />
                <div className="text-left">
                  <p className="text-xs text-muted-foreground">Verified on</p>
                  <p className="text-sm font-bold text-foreground">Trustpilot</p>
                  <div className="mt-0.5 flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-3 w-3 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                    <span className="ml-1 text-xs text-muted-foreground">4.8/5</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Infinite scroll testimonial cards - FULL WIDTH (outside container) */}
      <div className="mt-10 overflow-hidden">
        <InfiniteScrollRow items={TESTIMONIALS} />
      </div>

      {/* Row 3: Customer count + Review cards (mobile) + CTA - in container */}
      <div className="mx-auto mt-10 max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <p className="text-base text-foreground sm:text-lg">
          Join <span className="font-bold text-primary">5,546+</span> happy customers who rated us across trusted platforms
        </p>

        {/* Review cards - mobile only */}
        <div className="flex lg:hidden justify-center gap-3 mt-4">
          {/* G2 Reviews */}
          <a
            href="https://www.g2.com/products/zapify-instagram-dm-automation-tool/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-xl border border-border/50 bg-card/50 px-3 py-2 transition-colors hover:bg-card/80"
          >
            <Image
              src="/g2-logo.png"
              alt="G2 Reviews"
              width={32}
              height={32}
              className="h-8 w-8 rounded-full object-contain"
              style={{ maxWidth: '32px', maxHeight: '32px' }}
            />
            <div className="text-left">
              <p className="text-[10px] text-muted-foreground">Rated on</p>
              <p className="text-xs font-bold text-foreground">G2 Reviews</p>
              <div className="mt-0.5 flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="h-2.5 w-2.5 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
                <span className="ml-0.5 text-[10px] text-muted-foreground">4.9/5</span>
              </div>
            </div>
          </a>

          {/* Trustpilot */}
          <a
            href="https://www.trustpilot.com/review/zapify.pro"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-xl border border-border/50 bg-card/50 px-3 py-2 transition-colors hover:bg-card/80"
          >
            <Image
              src="/g2-logo.png"
              alt="Trustpilot"
              width={32}
              height={32}
              className="h-8 w-8 object-contain"
            />
            <div className="text-left">
              <p className="text-[10px] text-muted-foreground">Verified on</p>
              <p className="text-xs font-bold text-foreground">Trustpilot</p>
              <div className="mt-0.5 flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="h-2.5 w-2.5 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
                <span className="ml-0.5 text-[10px] text-muted-foreground">4.8/5</span>
              </div>
            </div>
          </a>
        </div>

        <button
          type="button"
          onClick={() => {
            track("cta_clicked", {
              cta_id: "CTA_Testimonials",
              button_text: "Join Today",
            });
            openAuth();
          }}
          className={cn(
            "mt-4 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary-gradient px-8 text-base font-bold text-white dark:text-black transition-opacity hover:opacity-90",
            themeColor === "yellow" && "!text-black"
          )}
        >
          Join Today
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
}
